"use client";
import React, { createContext, useContext, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register plugins (only register once to avoid errors)
if (typeof window !== "undefined") {
  gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);
}

interface GSAPContextType {
  gsap: typeof gsap;
  registerAnimation: (id: string, animation: gsap.core.Timeline) => void;
  killAnimation: (id: string) => void;
  killAllAnimations: () => void;
}

const GSAPContext = createContext<GSAPContextType | null>(null);

export const useGSAP = () => {
  const context = useContext(GSAPContext);
  if (!context) {
    throw new Error("useGSAP must be used within a GSAPProvider");
  }
  return context;
};

interface GSAPProviderProps {
  children: React.ReactNode;
}

export const GSAPProvider: React.FC<GSAPProviderProps> = ({ children }) => {
  const animations = useRef<Map<string, gsap.core.Timeline>>(new Map());

  const registerAnimation = (id: string, animation: gsap.core.Timeline) => {
    animations.current.set(id, animation);
  };

  const killAnimation = (id: string) => {
    const animation = animations.current.get(id);
    if (animation) {
      animation.kill();
      animations.current.delete(id);
    }
  };

  const killAllAnimations = () => {
    animations.current.forEach((animation) => {
      animation.kill();
    });
    animations.current.clear();
  };

  // Ensure clean animations on page changes
  useEffect(() => {
    // Set default GSAP configuration
    gsap.defaults({
      ease: "power2.out",
      overwrite: "auto",
    });

    return () => {
      killAllAnimations();
      // Clear any lingering ScrollTriggers
      if (ScrollTrigger) {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      }
    };
  }, []);

  const value: GSAPContextType = {
    gsap,
    registerAnimation,
    killAnimation,
    killAllAnimations,
  };

  return <GSAPContext.Provider value={value}>{children}</GSAPContext.Provider>;
};
